import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens, typographyStyles, Label } from "@fluentui/react-components";
import { ErrorCircle12Filled } from "@fluentui/react-icons";
import { AttachmentItem } from "../../../models/shared/AttachmentList";
import { Logger } from "../../../services/Logger";
import { SmartFilingManagerFluent9 } from "../../../services/SmartFiling/SmartFilingManagerFluent9";
import { InvalidateCacheService } from "../../../services/NewformaApi/InvalidateCacheService";
import { OfficeWrapper } from "../../../services/OfficeWrapper";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import LocalFilesSection from "./LocalFilesSection";
import { ProjectFilesSection } from "./ProjectFilesSection";
import AttachmentsListFluent9 from "./attachmentList/AttachmentsListFluent9";
import { MAX_ATTACHMENTS } from "./attachmentList/AttachmentListHelpers";
import { SharePointAuthServiceFactory } from "../../../services/SharePoint/SharePointAuthServiceFactory";

export interface AttachmentsComponentProps extends LocalizeContextProps {
    attachments: AttachmentItem[];
    otherPageAttachments?: AttachmentItem[];
    logger: Logger;
    disabled: boolean;
    onAttachmentsUpdated: (attachments: AttachmentItem[]) => void;
    allowFileUploads?: boolean;
    isCloudProject?: boolean;
    required?: boolean;
    isFilePathInAttachmentsSupported?: boolean;
    isNL1221Supported?: boolean;
    classNameForScroll?: string;
    updateMoreOptions?: () => void;
    expandMoreOptions?: () => void;
    isIncludeReviewerAttachment?: boolean;
    customErrors?: {
        duplicateFile?: string;
        maximumFiles?: string;
    };
    shouldDismissErrors?: boolean;
    onDismissErrorsHandled?: () => void;
    showValidationError?: boolean;
    /** Total attachment count across all sections (e.g. local + project files).
     *  Used for validation error display when attachments span multiple sources. */
    totalAttachmentCount?: number;
    projectFilesSection?: {
        smartFilingManager: SmartFilingManagerFluent9;
        invalidateCacheService: InvalidateCacheService;
        logger: Logger;
        officeWrapper: OfficeWrapper;
        projectsService: IProjectsService;
        sharePointAuthServiceFactory?: SharePointAuthServiceFactory;
        disabled?: boolean;
        selectedProjectAttachments: AttachmentItem[];
        onProjectAttachmentsUpdated: (attachments: AttachmentItem[]) => void;
    };
    children?: React.ReactNode;
    populateKey?: number;
}

const useStyles = makeStyles({
    filesContainer: {
        ...typographyStyles.subtitle2,
        display: "flex",
        alignItems: "center",
        gap: "2px",
        color: tokens.colorNeutralForeground1,
    },
    requiredMark: {
        color: tokens.colorPaletteRedForeground3,
        marginLeft: "2px",
    },
    validationError: {
        ...typographyStyles.caption1,
        color: tokens.colorPaletteRedForeground1,
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    sectionsContainer: {
        width: "100%",
    },
});

const AttachmentsFluent9Inner = (props: AttachmentsComponentProps) => {
    const styles = useStyles();
    const projectAttachments = props.projectFilesSection?.selectedProjectAttachments ?? [];
    const combinedAttachments = useMemo(() => [...props.attachments, ...projectAttachments], [
        props.attachments,
        projectAttachments,
    ]);
    const combinedMaxAttachments = MAX_ATTACHMENTS;

    const sectionsContainerRef = useRef<HTMLDivElement>(null);
    const isInitialMountRef = useRef(true);
    const prevAttachmentCount = useRef<number>(combinedAttachments.length);
    const [showLocalDropzone, setShowLocalDropzone] = useState(true);
    const [projectListResetKey, setProjectListResetKey] = useState(0);

    // Scroll into view when new attachments are added (from any section)
    useEffect(() => {
        prevAttachmentCount.current = combinedAttachments.length;
    }, [props.populateKey]);

    useEffect(() => {
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            prevAttachmentCount.current = combinedAttachments.length;
            return;
        }
        if (combinedAttachments.length > prevAttachmentCount.current && sectionsContainerRef.current) {
            sectionsContainerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        }
        prevAttachmentCount.current = combinedAttachments.length;
    }, [combinedAttachments.length]);

    useEffect(() => {
        if (props.attachments.length === 0) {
            setShowLocalDropzone(true);
        }
    }, [props.attachments.length]);

    const handleCombinedAttachmentsClear = useCallback(() => {
        props.onAttachmentsUpdated([]);
        props.projectFilesSection?.onProjectAttachmentsUpdated([]);
        setShowLocalDropzone(true);
    }, [props.onAttachmentsUpdated, props.projectFilesSection]);

    const handleProjectListDialogOpen = useCallback(() => {
        setProjectListResetKey((previousResetKey) => previousResetKey + 1);
    }, []);

    const handleCombinedAttachmentsRemove = useCallback(
        (itemsToRemove: AttachmentItem[]) => {
            if (itemsToRemove.length === 0) {
                return;
            }
            const removeSet = new Set<AttachmentItem>(itemsToRemove);

            const remainingLocal = props.attachments.filter((a) => !removeSet.has(a));
            props.onAttachmentsUpdated(remainingLocal);

            if (!props.projectFilesSection) {
                return;
            }
            // Use reference equality so same-NRN files in different folders are handled independently.
            const remainingProject = props.projectFilesSection.selectedProjectAttachments.filter(
                (a) => !removeSet.has(a)
            );
            props.projectFilesSection.onProjectAttachmentsUpdated(remainingProject);
        },
        [props.attachments, props.onAttachmentsUpdated, props.projectFilesSection]
    );

    return (
        <>
            {props.allowFileUploads ? (
                <>
                    <div className={styles.filesContainer}>
                        <Label>
                            {props.translate("SHARED.ATTACHMENTS.FILES") as string}
                            {props.required ? <span className={styles.requiredMark}> *</span> : null}
                        </Label>
                    </div>
                    {props.showValidationError && (props.totalAttachmentCount ?? props.attachments.length) === 0 ? (
                        <span className={styles.validationError}>
                            <ErrorCircle12Filled />
                            {props.translate("SHARED.FIELD.REQUIRED_FILE_ERROR") as string}
                        </span>
                    ) : null}
                </>
            ) : null}

            <div ref={sectionsContainerRef} className={styles.sectionsContainer}>
                {props.allowFileUploads ? (
                    <LocalFilesSection
                        attachments={props.attachments}
                        otherPageAttachments={props.otherPageAttachments}
                        logger={props.logger}
                        disabled={props.disabled}
                        onAttachmentsUpdated={props.onAttachmentsUpdated}
                        isCloudProject={props.isCloudProject}
                        isFilePathInAttachmentsSupported={props.isFilePathInAttachmentsSupported}
                        isNL1221Supported={props.isNL1221Supported}
                        classNameForScroll={props.classNameForScroll}
                        updateMoreOptions={props.updateMoreOptions}
                        expandMoreOptions={props.expandMoreOptions}
                        isIncludeReviewerAttachment={props.isIncludeReviewerAttachment}
                        customErrors={props.customErrors}
                        shouldDismissErrors={props.shouldDismissErrors}
                        onDismissErrorsHandled={props.onDismissErrorsHandled}
                        showDropzone={showLocalDropzone}
                        additionalAttachments={projectAttachments}
                    />
                ) : null}

                {props.projectFilesSection ? (
                    <ProjectFilesSection
                        smartFilingManager={props.projectFilesSection.smartFilingManager}
                        invalidateCacheService={props.projectFilesSection.invalidateCacheService}
                        logger={props.projectFilesSection.logger}
                        officeWrapper={props.projectFilesSection.officeWrapper}
                        projectsService={props.projectFilesSection.projectsService}
                        sharePointAuthServiceFactory={props.projectFilesSection.sharePointAuthServiceFactory}
                        selectedProjectAttachments={props.projectFilesSection.selectedProjectAttachments}
                        onProjectAttachmentsUpdated={props.projectFilesSection.onProjectAttachmentsUpdated}
                        onOpenDialog={handleProjectListDialogOpen}
                        existingAttachmentCount={combinedAttachments.length}
                        localAttachments={props.attachments}
                    />
                ) : null}

                {combinedAttachments.length > 0 ? (
                    <AttachmentsListFluent9
                        disabled={props.disabled}
                        attachmentsItems={combinedAttachments}
                        onItemRemove={handleCombinedAttachmentsRemove}
                        onClear={handleCombinedAttachmentsClear}
                        logger={props.logger}
                        maxAttachments={combinedMaxAttachments}
                        resetKey={props.projectFilesSection ? projectListResetKey : undefined}
                        onMoveIntoSubFolder={() => setShowLocalDropzone(false)}
                        onReturnToRoot={() => setShowLocalDropzone(true)}
                    />
                ) : null}

                {props.children}
            </div>
        </>
    );
};

export default withLocalize(AttachmentsFluent9Inner);
